import { useEffect, useRef, useState } from "react";
import Slider from "react-slick";
import clsx from "clsx";
import { useBreakpointContext } from "@/contexts/BreakpointContext";
import { PLACEHOLDER_IMAGE } from "@/constants/common";
import type { ProductImage } from "@/types/cart";

interface ProductImageSliderProps {
  images: ProductImage[];
  name?: string;
}

const ProductImageSlider = ({ images, name = "" }: ProductImageSliderProps) => {
  const { isDesktop } = useBreakpointContext();

  const [currentIndex, setCurrentIndex] = useState(0);
  const [thumbContainerHeight, setThumbContainerHeight] = useState(0);

  const sliderWrapperRef = useRef<HTMLDivElement | null>(null);
  const sliderRef = useRef<Slider | null>(null);

  const settings = {
    dots: true,
    arrows: false,
    infinite: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    beforeChange: (_current: number, next: number) => {
      setCurrentIndex(next);
    },
    customPaging: (index: number) => {
      return (
        <div
          className={clsx(
            "block w-32 rounded-lg border-4 lg:w-full",
            currentIndex === index ? "border-neutral bg-neutral" : "border-transparent",
          )}
        >
          <img
            src={images[index].src ?? PLACEHOLDER_IMAGE}
            srcSet={images[index].srcset}
            sizes={images[index].sizes}
            loading={index === 0 ? "eager" : "lazy"}
            decoding={index === 0 ? "async" : "auto"}
            alt={name || images[index].alt || ""}
            className="brand-product-placeholder aspect-square rounded-md lg:aspect-[7/8]"
          />
        </div>
      );
    },

    dotsClass:
      "brand-scrollbar-hidden relative lg:absolute left-0 lg:-left-[25%] lg:top-0 flex lg:flex-col mt-1.5 lg:mt-0 lg:w-[calc(25%-1%)] overflow-x-auto lg:overflow-x-hidden lg:overflow-y-auto",
    appendDots: (dots: React.ReactNode) => {
      return (
        <div
          style={{
            height: isDesktop && thumbContainerHeight > 0 ? `${thumbContainerHeight}px` : "auto",
          }}
        >
          <ul className="flex gap-1 lg:flex-col">{dots}</ul>
        </div>
      );
    },
  };

  useEffect(() => {
    // 僅在「桌面版」時才限制縮圖容器的高度（縮圖為垂直排列）
    // 手機版縮圖為水平排列，不需要限制高度，直接重置為 0
    if (!isDesktop) {
      setThumbContainerHeight(0);
      return;
    }

    // 依據目前主視圖第一張 slide 的實際高度，同步設定縮圖容器高度
    const updateThumbContainerHeight = (): void => {
      if (!sliderWrapperRef.current) return;

      const slideElements = sliderWrapperRef.current.querySelectorAll(".slick-slide");
      const slideElement = slideElements[0] as HTMLElement;
      if (slideElement) {
        const slideHeight = slideElement.clientHeight;
        setThumbContainerHeight(slideHeight);
      }
    };

    // 監聽 slider 外層容器尺寸變化
    const resizeObserver = new ResizeObserver(() => {
      updateThumbContainerHeight();
    });

    if (sliderWrapperRef.current) {
      resizeObserver.observe(sliderWrapperRef.current);
    }

    updateThumbContainerHeight();

    return () => {
      resizeObserver.disconnect();
    };
  }, [isDesktop]);

  return (
    <div className="slider-wrapper relative pl-0 lg:pl-[25%]" ref={sliderWrapperRef}>
      <Slider ref={sliderRef} {...settings}>
        {images.map((img, index) => (
          <div key={img.id}>
            <img
              src={img.src ?? PLACEHOLDER_IMAGE}
              srcSet={img.srcset}
              sizes={img.sizes}
              loading={index === 0 ? "eager" : "lazy"}
              decoding={index === 0 ? "sync" : "async"}
              alt={name || img.alt || ""}
              className="brand-product-placeholder rounded-md"
            />
          </div>
        ))}
      </Slider>
    </div>
  );
};

export default ProductImageSlider;
